import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authRoutingDebugLines, emptyAuthRoutingSnapshot, inspectAuthRouting, type AuthRoutingSnapshot } from "@/lib/auth-routing";

export const Route = createFileRoute("/auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Anmeldung – Energy Prime 360" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"login" | "signup" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [authCheck, setAuthCheck] = useState<"checking" | "login" | "redirecting" | "failed">("checking");
  const [debug, setDebug] = useState<AuthRoutingSnapshot>(() => emptyAuthRoutingSnapshot());
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (!cancelled) {
        setShowDebug(true);
        setAuthCheck((state) => (state === "redirecting" || state === "checking" ? "failed" : state));
        setError("Auth-/Routing-Prüfung hat länger als 5 Sekunden gedauert.");
      }
    }, 5_000);

    inspectAuthRouting().then((snapshot) => {
      if (cancelled) return;
      setDebug(snapshot);
      if (snapshot.error) {
        setAuthCheck("failed");
        setShowDebug(true);
        setError(snapshot.error);
        return;
      }
      if (!snapshot.sessionPresent) {
        window.clearTimeout(timeout);
        setAuthCheck("login");
        return;
      }
      if (!snapshot.targetRoute) {
        setAuthCheck("failed");
        setShowDebug(true);
        setError("Session vorhanden, aber keine Zielroute ermittelbar.");
        return;
      }
      const nextSnapshot = { ...snapshot, lastRedirectAttempt: snapshot.targetRoute };
      setDebug(nextSnapshot);
      setAuthCheck("redirecting");
      navigate({ to: snapshot.targetRoute, replace: true }).catch((navError) => {
        if (cancelled) return;
        setAuthCheck("failed");
        setShowDebug(true);
        setError(navError instanceof Error ? navError.message : JSON.stringify(navError));
      });
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [navigate]);

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null); setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setBusy(false); return setError(error.message); }
    const snapshot = await inspectAuthRouting();
    setDebug(snapshot);
    setBusy(false);
    if (snapshot.error) { setShowDebug(true); return setError(snapshot.error); }
    if (!snapshot.targetRoute) { setShowDebug(true); return setError("Login erfolgreich, aber keine Zielroute ermittelbar."); }
    setAuthCheck("redirecting");
    setDebug({ ...snapshot, lastRedirectAttempt: snapshot.targetRoute });
    navigate({ to: snapshot.targetRoute, replace: true }).catch((navError) => {
      setShowDebug(true);
      setError(navError instanceof Error ? navError.message : JSON.stringify(navError));
    });
  }

  async function onSignup(e: FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null); setBusy(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin + "/auth",
        data: { full_name: fullName },
      },
    });
    setBusy(false);
    if (error) return setError(error.message);
    setInfo("Konto angelegt. Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link, den wir Ihnen geschickt haben.");
  }

  async function onForgot(e: FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null); setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/reset-password",
    });
    setBusy(false);
    if (error) return setError(error.message);
    setInfo("Falls die E-Mail-Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen geschickt.");
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Energy Prime 360</CardTitle>
          <CardDescription>Geschützter Bereich – Anmeldung erforderlich</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => { setError(null); setInfo(null); setTab(v as typeof tab); }}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Registrieren</TabsTrigger>
              <TabsTrigger value="forgot">Passwort</TabsTrigger>
            </TabsList>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}
            {info && <Alert className="mt-4"><AlertDescription>{info}</AlertDescription></Alert>}
            {(showDebug || authCheck === "redirecting") && <AuthDebugPanel snapshot={debug} />}

            <TabsContent value="login">
              <form onSubmit={onLogin} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="l-email">E-Mail</Label>
                  <Input id="l-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="l-pwd">Passwort</Label>
                  <Input id="l-pwd" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>{busy ? "…" : "Anmelden"}</Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={onSignup} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="s-name">Voller Name</Label>
                  <Input id="s-name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="s-email">E-Mail</Label>
                  <Input id="s-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="s-pwd">Passwort (mind. 8 Zeichen)</Label>
                  <Input id="s-pwd" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>{busy ? "…" : "Konto erstellen"}</Button>
              </form>
            </TabsContent>

            <TabsContent value="forgot">
              <form onSubmit={onForgot} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="f-email">E-Mail</Label>
                  <Input id="f-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>{busy ? "…" : "Link zum Zurücksetzen senden"}</Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            <Link to="/" className="underline">Zurück zur Startseite</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function AuthDebugPanel({ snapshot }: { snapshot: AuthRoutingSnapshot }) {
  return (
    <div className="mt-4 rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
      {authRoutingDebugLines(snapshot).map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 py-0.5">
          <span>{label}</span>
          <span className="max-w-56 truncate text-right font-mono text-foreground">{value}</span>
        </div>
      ))}
    </div>
  );
}
